import { type DocumentNode, useApolloClient } from '@apollo/client';
import { Button, Row, Typography, message } from 'antd';
import { isNil } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyTheme } from '../../api/types';
import { useThemeComponents } from '../../themes';
import { importExcelExtension } from '../../utilities/extensions';
import getApolloErrors from '../../utilities/getApolloErrors';
import { useAccountContext } from '../contexts/AccountContextManager';
import CancelButton from '../modal/CancelButton';
import OkButton from '../modal/OkButton';
import ErrorContent from './ErrorContent';
import { ContentContainer, UploadContainer } from './ui';

type SupportingSuccess = {
    setUpdatedCount: (newCount: null | number) => void;
    setError: (newError: null | string) => void;
};

export type UseImportModalProps<TMutation, TMutationVariables> = {
    title: string;
    uploadMutation: DocumentNode;
    getVariables: (file: File) => TMutationVariables;
    refetchQueries: DocumentNode[];
    onSuccess?: (data: TMutation, supporting: SupportingSuccess) => void;
    downloadTemplate: (token: string | null) => Promise<void>;
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    downloadSuccessMessage: string;
    updatedCountTransKey: string;
};

export type ImportModalProps = {
    title: string;
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    onUpload: (file: File) => Promise<void>;
    error: string | null;
    updatedCount: number | null;
    downloadTemplate: (token: string | null) => Promise<void>;
    isSubmitting: boolean;
    downloadSuccessMessage: string;
    updatedCountTransKey: string;
};

const ImportModal = ({
    title,
    isOpen,
    setOpen,
    onUpload,
    downloadTemplate,
    error,
    updatedCount,
    isSubmitting,
    downloadSuccessMessage,
    updatedCountTransKey,
}: ImportModalProps) => {
    const { t } = useTranslation(['importExportExcel']);
    const { token } = useAccountContext();
    const { theme, Modal } = useThemeComponents();

    const onDownloadClick = useCallback(async () => {
        await downloadTemplate(token);

        message.success({
            content: downloadSuccessMessage,
            key: 'primary',
        });
    }, [downloadSuccessMessage, downloadTemplate, token]);

    const onCancelHandler = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const onChangeHandler = useCallback(
        ({ file }) => {
            if (file instanceof File) {
                onUpload(file);
            }
        },
        [onUpload]
    );

    const content = useMemo(() => {
        if (!isNil(error)) {
            return <ErrorContent errors={error} />;
        }

        if (!isNil(updatedCount)) {
            return (
                <Row align="middle" gutter={[14, 14]} justify="center">
                    <Typography>{t(updatedCountTransKey, { count: updatedCount })}</Typography>
                </Row>
            );
        }

        return (
            <Row align="middle" gutter={[14, 14]} justify="center">
                <Typography>{t('importExportExcel:description.message')}</Typography>
                <Typography>{t('importExportExcel:description.note')}</Typography>
                <ContentContainer direction="horizontal">
                    <Button onClick={onDownloadClick} type="link">
                        {t('importExportExcel:description.downloadTemplate')}
                    </Button>
                </ContentContainer>
            </Row>
        );
    }, [error, updatedCount, t, onDownloadClick, updatedCountTransKey]);

    const footer = useMemo(() => {
        const cancelButton = (
            <CancelButton key="close" onClick={onCancelHandler}>
                {t('importExportExcel:actions.close')}
            </CancelButton>
        );

        if (!isNil(error) || !isNil(updatedCount)) {
            return [cancelButton];
        }

        const okButton = (
            <UploadContainer
                accept={importExcelExtension.join(',')}
                beforeUpload={() => false}
                onChange={onChangeHandler}
                showUploadList={false}
            >
                <OkButton key="submit" loading={isSubmitting}>
                    {t('importExportExcel:actions.uploadFile')}
                </OkButton>
            </UploadContainer>
        );

        if (theme === CompanyTheme.PorscheV3 || theme === CompanyTheme.Porsche) {
            return [okButton, cancelButton];
        }

        return [cancelButton, okButton];
    }, [error, updatedCount, isSubmitting, onChangeHandler, onCancelHandler, theme, t]);

    return (
        <Modal footer={footer} onCancel={onCancelHandler} open={isOpen} title={title} width={550}>
            {content}
        </Modal>
    );
};

const useImportModal = <TMutation, TMutationVariables>({
    title,
    uploadMutation,
    getVariables,
    refetchQueries,
    downloadTemplate,
    onSuccess,
    isOpen,
    setOpen,
    downloadSuccessMessage,
    updatedCountTransKey,
}: UseImportModalProps<TMutation, TMutationVariables>) => {
    const apolloClient = useApolloClient();
    const [isSubmitting, setSubmitting] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [updatedCount, setUpdatedCount] = useState<number | null>(null);

    const onOpenChange = useCallback(
        (newOpen: boolean) => {
            setUpdatedCount(null);
            setError(null);
            setOpen(newOpen);
        },
        [setOpen]
    );

    const onUpload = useCallback(
        async (file: File) => {
            try {
                setSubmitting(true);

                const variables = getVariables(file);

                const { data } = await apolloClient.mutate<TMutation, TMutationVariables>({
                    mutation: uploadMutation,
                    variables,
                    refetchQueries,
                });

                if (onSuccess) {
                    onSuccess(data, { setUpdatedCount, setError });
                }

                setSubmitting(false);
            } catch (error) {
                const apolloErrors = getApolloErrors(error);
                if (apolloErrors?.$root) {
                    setError(apolloErrors?.$root);
                }

                setSubmitting(false);
            }
        },
        [apolloClient, getVariables, onSuccess, refetchQueries, uploadMutation]
    );

    return {
        updatedCount,
        isSubmitting,
        render: () => (
            <ImportModal
                downloadSuccessMessage={downloadSuccessMessage}
                downloadTemplate={downloadTemplate}
                error={error}
                isOpen={isOpen}
                isSubmitting={isSubmitting}
                onUpload={onUpload}
                setOpen={onOpenChange}
                title={title}
                updatedCount={updatedCount}
                updatedCountTransKey={updatedCountTransKey}
            />
        ),
    };
};

export default useImportModal;
